import { X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTelegramWebhook } from "@/hooks/useTelegramWebhook";

interface Message {
  id: string;
  message: string;
  sender: string;
  timestamp: string;
  session_id: string;
  status: string;
}

const WhatsAppWidget = () => {
  const [showWidget, setShowWidget] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState("");
  const [userInput, setUserInput] = useState("");
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [isTyping, setIsTyping] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [sessionCreated, setSessionCreated] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sendSoundRef = useRef<HTMLAudioElement>(null);
  const receiveSoundRef = useRef<HTMLAudioElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const channelRef = useRef<any>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const { triggerWebhook } = useTelegramWebhook();

  useEffect(() => {
    // Create audio elements for sound notifications
    const sendSound = new Audio('data:audio/wav;base64,UklGRvIAAABXQVZFZm10IBAAAAABAAEAESsAABErAAABAAgAZGF0YQoAAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvWgfCEOSy+/Kh0sOB1e18N2QQAoUXrTp66hVFApGn+DyvWgfCEOSy+/Kh0sOBw==');
    const receiveSound = new Audio('data:audio/wav;base64,UklGRvIAAABXQVZFZm10IBAAAAABAAEAESsAABErAAABAAgAZGF0YQoAAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvWgfCEOSy+/Kh0sOB1e18N2QQAoUXrTp66hVFApGn+DyvWgfCEOSy+/Kh0sOBw==');
    
    sendSoundRef.current = sendSound;
    receiveSoundRef.current = receiveSound;

    // Generate a unique session ID for this visitor
    const storedSessionId = localStorage.getItem('visitor_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = crypto.randomUUID();
      localStorage.setItem('visitor_session_id', newSessionId);
      setSessionId(newSessionId);
    }

    // Store the widget show time to freeze first message time
    const widgetShowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Update current time for new messages, but keep frozen time for first message
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    };
    
    setCurrentTime(widgetShowTime); // Set initial frozen time
    const timeInterval = setInterval(updateTime, 60000);

    // Show widget after 10 seconds OR 5 seconds after scrolling to bottom
    const timer = setTimeout(() => {
      setShowWidget(true);
      setMessageIndex(0);
      playSound('receive'); // Play sound when widget appears
      
      const messageTimer = setInterval(() => {
      setMessageIndex(prev => {
        if (prev >= 0) { // Show only first message
          clearInterval(messageTimer);
          return prev;
        }
        return prev + 1;
      });
      }, 2000);
    }, 10000);

    // Show widget 5 seconds after scrolling to bottom
    let scrollToBottomTimer: NodeJS.Timeout;
    const handleScrollToBottom = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Check if user has scrolled to bottom (within 100px threshold)
      if (scrollTop + windowHeight >= documentHeight - 100) {
        if (scrollToBottomTimer) clearTimeout(scrollToBottomTimer);
        
        scrollToBottomTimer = setTimeout(() => {
          if (!showWidget) {
            setShowWidget(true);
            setMessageIndex(0);
            playSound('receive');
            
            const messageTimer = setInterval(() => {
            setMessageIndex(prev => {
              if (prev >= 0) {
                clearInterval(messageTimer);
                return prev;
              }
              return prev + 1;
            });
            }, 2000);
          }
        }, 5000);
      } else {
        if (scrollToBottomTimer) clearTimeout(scrollToBottomTimer);
      }
    };

    window.addEventListener('scroll', handleScrollToBottom, { passive: true });

    // Listen for external trigger to show widget
    const handleShowWidget = () => {
      setShowWidget(true);
      setCurrentTime(widgetShowTime); // Use frozen time when manually opened
      // Broadcast state change
      window.dispatchEvent(new CustomEvent('whatsAppWidgetStateChange', { 
        detail: { isOpen: true } 
      }));
    };

    // Listen for external trigger to hide widget
    const handleHideWidget = () => {
      setShowWidget(false);
      // Broadcast state change
      window.dispatchEvent(new CustomEvent('whatsAppWidgetStateChange', { 
        detail: { isOpen: false } 
      }));
    };

    window.addEventListener('showWhatsAppWidget', handleShowWidget);
    window.addEventListener('hideWhatsAppWidget', handleHideWidget);

    return () => {
      clearTimeout(timer);
      clearInterval(timeInterval);
      if (scrollToBottomTimer) clearTimeout(scrollToBottomTimer);
      window.removeEventListener('scroll', handleScrollToBottom);
      window.removeEventListener('showWhatsAppWidget', handleShowWidget);
      window.removeEventListener('hideWhatsAppWidget', handleHideWidget);
    };
  }, []);

  useEffect(() => {
    if (showWidget && sessionId && !sessionCreated) {
      // Create session when widget shows but don't send notification
      createSession();
      setSessionCreated(true);
    }
  }, [showWidget, sessionId, sessionCreated]);

  useEffect(() => {
    if (sessionId && sessionCreated) {
      // Load existing messages and setup realtime
      loadExistingMessages();
      setupRealtimeSubscription();
    }

    // Handle page visibility changes to reconnect when tab becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && sessionId && sessionCreated) {
        console.log('Page became visible, reconnecting...');
        setupRealtimeSubscription();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount or session change
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [sessionId, sessionCreated]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, adminTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const playSound = (type: 'send' | 'receive') => {
    const sound = type === 'send' ? sendSoundRef.current : receiveSoundRef.current;
    if (sound) {
      sound.currentTime = 0;
      sound.volume = 0.3;
      sound.play().catch(console.error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!widgetRef.current) return;
    
    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !widgetRef.current) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep widget within viewport bounds
    const rect = widgetRef.current.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width;
    const maxY = window.innerHeight - rect.height;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch('https://ipinfo.io?token=fdb6cbddb142df');
      const data = await response.json();
      
      return {
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        ip: data.ip || 'Unknown',
        browser: `${getBrowserName()} on ${navigator.platform}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      };
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return {
        city: 'Unknown',
        region: 'Unknown',
        ip: 'Unknown',
        browser: `${getBrowserName()} on ${navigator.platform}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      };
    }
  };

  const getBrowserName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  };

  const createSession = async () => {
    if (!sessionId) return;
    
    try {
      console.log('Creating chat session:', sessionId);
      const userInfo = await fetchUserInfo();
      const sessionName = `${userInfo.city} | ${getBrowserName()} | ${userInfo.ip.split('.').slice(0, 2).join('.')}.*`;

      const { error } = await supabase
        .from('chat_sessions')
        .upsert({
          id: sessionId,
          session_name: sessionName,
          last_message_at: new Date().toISOString(),
          unread_count: 0,
          session_metadata: userInfo
        }, {
          onConflict: 'id'
        });

      if (error) {
        console.error('Error creating session:', error);
        // Silenced user-facing toast per request
        return;
      }

      console.log('Chat session created/updated successfully');
      
    } catch (error) {
      console.error('Error in createSession:', error);
      // Silenced user-facing toast per request
    }
  };

  const setupRealtimeSubscription = () => {
    // Clean up existing channel properly with a delay
    if (channelRef.current) {
      console.log('Cleaning up existing channel...');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Small delay to ensure cleanup is complete
    setTimeout(() => {
      // Subscribe to realtime updates for this specific session with unique channel name
      const channel = supabase
        .channel(`visitor-session-${sessionId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `session_id=eq.${sessionId}`
          },
          async (payload) => {
            const newMessage = payload.new as Message;
            console.log('Visitor received new message:', newMessage);
            
            // Add any message to the chat (visitor or admin)
            setChatMessages(prev => {
              const exists = prev.some(msg => msg.id === newMessage.id);
              if (exists) return prev;
              return [...prev, newMessage];
            });
            
            if (newMessage.sender === 'admin') {
              playSound('receive');
              
              // Show chat widget if it's closed and auto-open it
              if (!showWidget) {
                setShowWidget(true);
                setMessageIndex(0); // Show only first message
                playSound('receive'); // Play sound when widget pops up
              }
              
              // Mark admin message as delivered after a short delay
              setTimeout(() => {
                supabase
        .from('chat_messages')
                  .update({ status: 'delivered' })
                  .eq('id', newMessage.id)
                  .then(() => console.log('Admin message marked as delivered'));
              }, 500);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'chat_messages',
            filter: `session_id=eq.${sessionId}`
          },
          (payload) => {
            const updatedMessage = payload.new as Message;
            setChatMessages(prev => 
              prev.map(msg => 
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
          }
        )
        .on('broadcast', { event: 'typing' }, (payload) => {
          const { session_id, typing, sender } = payload.payload;
          if (sender === 'admin' && session_id === sessionId) {
            setAdminTyping(typing);
          }
        })
        .subscribe((status) => {
          console.log('Visitor subscription status:', status);
          
          // Handle different subscription states
          if (status === 'SUBSCRIBED') {
            console.log('Successfully reconnected to realtime');
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.log('Connection failed, will retry on next visibility change');
            // Clean up failed connection
            if (channelRef.current) {
              supabase.removeChannel(channelRef.current);
              channelRef.current = null;
            }
          }
        });

      channelRef.current = channel;
    }, 100); // Small delay for cleanup
  };

  const loadExistingMessages = async () => {
    console.log('Loading messages for session:', sessionId);
    const { data, error } = await supabase
        .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('timestamp', { ascending: true });

    if (!error && data) {
      console.log('Loaded existing messages:', data);
      setChatMessages(data);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
    
    if (!isTyping && e.target.value.length > 0) {
      setIsTyping(true);
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'typing',
          payload: { session_id: sessionId, typing: true, sender: 'visitor' }
        });
      }
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: 'typing',
          payload: { session_id: sessionId, typing: false, sender: 'visitor' }
        });
      }
    }, 1000);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const messageText = userInput.trim();
    setUserInput("");

    // Fire GTM event for first chat message
    if (chatMessages.length === 0) {
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({ event: 'cta_first_chat_message' });
      }
    }

    // Open WhatsApp chat initiation only
    const waText = messageText;
    window.open('https://wa.me/4915754767196?text=' + encodeURIComponent(waText), '_blank');
  };

  const handleCloseWidget = () => {
    setShowWidget(false);
    // Broadcast state change
    window.dispatchEvent(new CustomEvent('whatsAppWidgetStateChange', { 
      detail: { isOpen: false } 
    }));
  };

  const getMessageStatus = (message: Message) => {
    if (message.sender === 'visitor') {
      switch (message.status) {
        case 'sent':
          return <span className="text-green-200">✓</span>;
        case 'delivered':
          return <span className="text-green-200">✓✓</span>;
        case 'read':
          return <span className="text-blue-300">✓✓</span>;
        default:
          return '';
      }
    }  
    return '';
  };

  const [frozenMessageTime] = useState(() => 
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );

  const messages = [
    "Hi! 👋 I’m here to help you pick wholesale vintage on camera. Need a quick call? You can request a **Zoom link** here or just send a message to start chat."
  ];

  const renderMessageWithLinks = (message: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const boldRegex = /\*\*(.*?)\*\*/g;
    
    // First handle bold text
    let processedMessage = message.replace(boldRegex, '<strong>$1</strong>');
    
    // Then handle URLs
    const parts = processedMessage.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline hover:text-blue-300 transition-all duration-300 font-semibold"
            style={{
              textShadow: '0 0 8px rgba(96, 165, 250, 0.6)',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          >
            {part}
          </a>
        );
      }
      // Render text with bold formatting
      return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
    });
  };

  // Calculate initial position - centered on mobile, positioned above mini widget on desktop
  const getInitialPosition = () => {
    if (position.x === 0 && position.y === 0) {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        return {
          top: '90px',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translateX(-50%)',
          marginLeft: '0',
          marginRight: '0'
        };
      } else {
        // Position above mini widget and align right edge with mini widget
        // Mini widget is at bottom: 16px (4 * 4px), right: 16px (4 * 4px), width: 570px
        // Chat widget width: 320px (w-80), so we need right: 16px to align right edges
        return {
          top: '90px',
          right: '16px', // Align with mini widget right edge
          left: 'auto',
          bottom: 'auto',
          transform: 'none'
        };
      }
    }
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none'
    };
  };

  return (
    <>
      {showWidget && (
        <div 
          ref={widgetRef}
          className={`fixed z-40 w-80 max-w-[calc(100vw-1rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 ${isDragging ? 'cursor-grabbing' : 'cursor-auto'}`}
          style={{
            ...getInitialPosition(),
            transition: isDragging ? 'none' : 'all 0.3s ease'
          }}
          data-whatsapp-widget
        >
          {/* Header - Draggable */}
          <div 
            className={`bg-[#075E54] text-white p-4 rounded-t-2xl flex items-center justify-between ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center space-x-3">
              <img 
                src="/images/sara-headshot.png"
                alt="Sarah"
                width={40} height={40}
                className="w-10 h-10 rounded-full object-cover object-top border-2 border-white/20"
              />
              <div>
                <div className="font-semibold text-sm">Rebecca (Chat via WhatsApp)</div>
                <div className="text-xs text-green-100 flex items-center">
                  {adminTyping ? (
                    <>
                      <div className="w-2 h-2 bg-green-300 rounded-full mr-1"></div>
                      typing...
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-green-300 rounded-full mr-1 animate-pulse"></div>
                      online
                    </>
                  )}
                </div>
              </div>
            </div>
            <button 
              onClick={handleCloseWidget}
              className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white/10 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="p-4 h-80 overflow-y-auto bg-[#ECE5DD] space-y-3" style={{
            backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"40\" height=\"40\" viewBox=\"0 0 40 40\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"%23f0f0f0\" fill-opacity=\"0.3\"%3E%3Cpath d=\"M20 20c0 11.046-8.954 20-20 20v-40c11.046 0 20 8.954 20 20zM0 20v20h40V0H20c0 11.046-8.954 20-20 20z\"/%3E%3C/g%3E%3C/svg%3E')"
          }}>
            {/* Initial messages */}
            {messages.slice(0, messageIndex + 1).map((message, index) => (
              <div key={`initial-${index}`} className="animate-in slide-in-from-left-3 duration-300">
                <div className="flex items-end space-x-2">
                  <img 
                    src="/images/sara-headshot.png"
                    alt="Sarah"
                    width={24} height={24}
                    className="w-6 h-6 rounded-full object-cover object-top"
                  />
                   <div className="bg-white p-3 rounded-lg rounded-bl-sm shadow-sm max-w-xs relative">
                     <div className="absolute -left-2 bottom-0 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-white border-b-8 border-b-transparent transform rotate-45"></div>
                     <p className="text-sm text-gray-800">{renderMessageWithLinks(message)}</p>
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <button
                          onClick={() => {
                            const text = 'Hi, please share a Zoom meeting link so I can start the live cam-picking session.';
                            window.open('https://wa.me/4915754767196?text=' + encodeURIComponent(text), '_blank');
                          }}
                          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-all duration-300"
                          style={{ animation: 'pulse 4s ease-in-out infinite' }}
                        >
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-ping"></div>
                          🎥 Request a Zoom Meeting
                        </button>
                      </div>
                       <p className="text-xs text-gray-500 mt-2">
                         {frozenMessageTime}
                       </p>
                   </div>
                </div>
              </div>
            ))}

            {/* Chat messages */}
            {chatMessages.map((message) => (
              <div key={message.id} className="animate-in slide-in-from-bottom-2 duration-200">
                {message.sender === 'visitor' ? (
                  <div className="flex justify-end">
                    <div className="bg-[#DCF8C6] p-3 rounded-lg rounded-br-sm shadow-sm max-w-xs relative">
                      <div className="absolute -right-2 bottom-0 w-0 h-0 border-l-8 border-l-[#DCF8C6] border-r-8 border-r-transparent border-b-8 border-b-transparent transform -rotate-45"></div>
                       <p className="text-sm text-gray-800">{renderMessageWithLinks(message.message)}</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <p className="text-xs text-gray-600">
                          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {getMessageStatus(message)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-end space-x-2">
                    <img 
                      src="/images/sara-headshot.png"
                      alt="Sarah"
                      className="w-6 h-6 rounded-full object-cover object-top"
                    />
                    <div className="bg-white p-3 rounded-lg rounded-bl-sm shadow-sm max-w-xs relative">
                      <div className="absolute -left-2 bottom-0 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-white border-b-8 border-b-transparent transform rotate-45"></div>
                      <p className="text-sm text-gray-800">{renderMessageWithLinks(message.message)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Admin typing indicator */}
            {adminTyping && (
              <div className="flex items-end space-x-2 animate-in fade-in duration-300">
                <img 
                  src="/images/sara-headshot.png"
                  alt="Sarah"
                  className="w-6 h-6 rounded-full object-cover object-top"
                />
                <div className="bg-white p-3 rounded-lg rounded-bl-sm shadow-sm relative">
                  <div className="absolute -left-2 bottom-0 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-white border-b-8 border-b-transparent transform rotate-45"></div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input form */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Type a message..."
                value={userInput}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                className="flex-1 rounded-full border-gray-300 focus:border-[#075E54] focus:ring-[#075E54]"
                disabled={isLoading}
              />
                <Button 
                onClick={handleSendMessage}
                disabled={!userInput.trim() || isLoading}
                className="bg-[#075E54] hover:bg-[#064A42] text-white rounded-full w-10 h-10 p-0 flex items-center justify-center"
              >
                <Send className="w-4 h-4 rotate-45" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppWidget;
